import type { IIconProps } from '../DataDisplay/Icon/Icon';
import { MenuImagesNames } from './assets/types';

export type Link = {
  href: string;
  title: string;
  trackingId?: string;
  locale?: string;
};

export type CardWIcon = Link & {
  icon: IIconProps['icon'];
  description?: string;
};

export type CardWImageMobileOverride = {
  icon: IIconProps['icon'];
};

export type CardWImage = Link & {
  image: MenuImagesNames;
  imagePos?: 'top' | 'bottom';
  description?: string;
  cta?: string;
  // override from image card to icon card in mobile nav
  mobileOverride?: CardWImageMobileOverride;
};

export type TabsFeatured = {
  title?: string;
  image: CardWImage;
};

export type Tabs = {
  id: string;
  type: 'tabs';
  title: string;
  children: Array<Tab>;
  plainTabList?: boolean;
  featured?: TabsFeatured;
  tabListLink?: Link;
  tabListGradient?: boolean;
};

export type RowItem = {
  title?: string;
  children: Array<CardWIcon | CardWImage | RoundedButtonLink>;
  columns?: 1 | 2;
};

export type TabList = {
  // TODO: this `id` appears to only be used for internal purposes and would be
  //   better to eliminate/randomize with something like `crypto.randomUUID()`.
  id: string;
  type: 'list';
  title: string;
  compact?: boolean;
  children: Array<
    | (CardWIcon & { isOverview?: boolean })
    | CardWImage
    | (Link & { isRoundedButton?: boolean })
  >;
};

export type TabRows = {
  id: string;
  type: 'rows';
  title: string;
  children: RowItem[];
};

export type TabColumns = {
  id: string;
  type: 'columns';
  title: string;
  children:
    | [ColumnItem, ColumnItem]
    | [ColumnItem, ColumnItem, ColumnItem | ImageColumnItem];
};

export type Tab = TabList | TabRows | TabColumns;

export type ColumnItem = {
  title: string;
  children?: CardWIcon[] | CardWImage[] | Link[];
};

export type ImageColumnItem = {
  title?: string;
  image: CardWImage;
  mobileOverride?: CustomerStoriesColumnItem;
};

export type CustomerStoryItem = {
  title: string;
  href: string;
  image: string;
};

export type CustomerStoriesColumnItem = {
  title?: string;
  stories: [CustomerStoryItem, CustomerStoryItem, CustomerStoryItem];
  seeAllLink: {
    title: string;
    href: string;
  };
};

export type Columns = {
  id: string;
  type: 'columns';
  title: string;
  children: [
    ColumnItem,
    ColumnItem,
    ColumnItem | ImageColumnItem | CustomerStoriesColumnItem,
  ];
};

export const isImageColumnItem = (obj: any): obj is ImageColumnItem => {
  return typeof obj === 'object' && obj !== null && 'image' in obj;
};

export const isCustomerStoriesColumnItem = (
  obj: any,
): obj is CustomerStoriesColumnItem => {
  return typeof obj === 'object' && obj !== null && 'stories' in obj;
};

export type List = {
  id: string;
  type: 'list';
  title: string;
  children?: Array<CardWIcon | CardWImage | RoundedButtonLink>;
  columns?: number;
  columnsWide?: boolean;
};

export type NavDataItems = Array<
  (Link & { id: string }) | Tabs | Columns | List
>;

export type NavData = {
  items: NavDataItems;
  ctaTrackingIds: { getAPIkeys: string };
};

export type SubMenuData = Tabs | Columns | List;

export const isLink = (obj: any): obj is Link => {
  return typeof obj === 'object' && obj !== null && !('children' in obj);
};

export const isCardWIcon = (obj: any): obj is CardWIcon => {
  return typeof obj === 'object' && obj !== null && 'icon' in obj;
};

export const isCardWImage = (obj: any): obj is CardWImage => {
  return typeof obj === 'object' && obj !== null && 'image' in obj;
};

export type RoundedButtonLink = Link & { isRoundedButton: true };

export const isRoundedButtonLink = (obj: any): obj is RoundedButtonLink => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'isRoundedButton' in obj &&
    obj.isRoundedButton === true
  );
};
